<template>
  <div>
    <property-report-card
      :hide-header="deleteEnabled"
      :is-empty="filteredProperties.length === 0"
      :is-loading="loading"
      :printing-mode="printingMode"
      description="Benchmark your property against the most statistically similar assets in the market."
      title="Rent Comps"
    >
      <template #loading>
        <q-markup-table bordered dense flat>
          <tbody>
            <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <td></td>
              <td v-for="x in skeletonColumns.length" :key="x">
                <q-skeleton animation="blink" type="text" />
              </td>
              <td v-for="x in 4" :key="x" style="width: 66px">
                <q-skeleton animation="blink" type="text" />
              </td>
              <td v-if="deleteEnabled" />
            </tr>
            <tr
              v-for="x in skeletonRows"
              :id="`intercom-property-compare-building-info-row-${x}`"
              :key="x"
            >
              <td :id="`intercom-address-${x}`" :style="skeletonStyles.address" class="no-padding">
                <div class="flex no-wrap full-height">
                  <q-skeleton
                    v-if="$q.screen.gt.sm"
                    animation="blink"
                    square
                    style="height: 114px; width: 175px"
                    type="rect"
                  />
                  <div class="q-px-md q-my-auto">
                    <q-skeleton animation="blink" style="width: 100px" type="text" />
                    <q-skeleton animation="blink" style="width: 150px" type="text" />
                    <q-skeleton animation="blink" style="width: 125px" type="text" />
                    <q-skeleton animation="blink" style="width: 100px" type="text" />
                    <q-skeleton animation="blink" style="width: 225px" type="text" />
                  </div>
                </div>
              </td>
              <td
                v-for="skeletonColumn in skeletonColumns"
                :id="`${skeletonColumn.intercomId}-${x}`"
                :key="skeletonColumn.field"
                :style="skeletonColumn.style"
              >
                <q-skeleton
                  :type="skeletonColumn.field === Column.SIMILARITY ? 'circle' : 'text'"
                  animation="blink"
                  class="q-mx-auto"
                />
              </td>
              <td :id="`intercom-avg_rent-${x}`" class="no-padding" colspan="4">
                <q-markup-table dense flat>
                  <tr v-for="i in 4" :key="i">
                    <td v-for="j in 4" :key="j" style="width: 25%">
                      <q-skeleton animation="blink" type="text" />
                    </td>
                  </tr>
                </q-markup-table>
              </td>
              <td v-if="deleteEnabled" :style="skeletonStyles.actions">
                <q-skeleton animation="blink" class="q-mx-auto" size="30px" type="circle" />
              </td>
            </tr>
          </tbody>
        </q-markup-table>
      </template>
      <template #actions>
        <q-select
          v-model="metricSelection"
          :options="metricOptions"
          dense
          label="Metric"
          outlined
          style="width: 150px"
          transition-duration="100"
          @update:model-value="
            (value) => $tracker.changedFilter('metric', 'rent_comps', value.value)
          "
        />
      </template>
      <q-table
        :class="{ 'sticky-actions-table': deleteEnabled }"
        :columns="columns"
        :rows="rows"
        :rows-per-page-options="[0]"
        :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        bordered
        dense
        flat
        hide-bottom
        row-key="id"
      >
        <template #header-cell-avg_rent>
          <th scope="col" style="width: 66px"></th>
          <th scope="col" style="width: 66px">
            {{ metricSelection.value === 'rent' ? 'Rent' : 'NER' }}
            <q-tooltip max-width="250px">
              <b>{{ metricSelection.value === 'rent' ? 'Rent' : 'NER' }}</b>
              <br />
              {{ metricSelection.value === 'rent' ? 'Asking rent' : 'Effective rent' }} for the unit
              type in the building. You can toggle between effective or asking rent, and select a
              date range to filter the data.
            </q-tooltip>
          </th>
          <th scope="col" style="width: 66px">
            SF
            <q-tooltip max-width="250px">
              <b>Square Footage</b>
              <br />
              Average square footage for the unit type in the building. You can select a date range
              to filter the data.
            </q-tooltip>
          </th>
          <th scope="col" style="width: 66px">
            PSF
            <q-tooltip max-width="250px">
              <b>PSF</b>
              <br />
              Average rent per square foot for the unit type in the building. You can toggle between
              effective or asking rent, and select a date range to filter the data.
            </q-tooltip>
          </th>
        </template>
        <template #header-cell="props">
          <q-th :props="props" @click="sortingChanged(props.col.name)">
            {{ props.col.label }}
            <q-tooltip v-if="props.col.description" max-width="250px">
              <b>{{ props.col.label }}</b>
              <br />
              {{ props.col.description }}
            </q-tooltip>
          </q-th>
        </template>
        <template #body="props">
          <q-tr
            :id="`intercom-property-compare-building-info-row-${props.rowIndex}`"
            :class="{
              'bg-deep-purple-light': subjectDetail && props.row.id === subjectDetail.id,
              'drop-target': deleteEnabled && draggedOverIndex === props.rowIndex
            }"
            :draggable="deleteEnabled"
            :props="props"
            class="avoid-break"
            @dragend="resetDragState"
            @dragstart="deleteEnabled ? handleDragStart($event, props.rowIndex) : null"
            @drop="deleteEnabled ? handleDrop(props.rowIndex) : null"
            @dragover.prevent="deleteEnabled ? handleDragOver($event, props.rowIndex) : null"
          >
            <q-td
              v-for="col in props.cols"
              :id="`intercom-${col.name}-${props.rowIndex}`"
              :key="col.name"
              :class="{
                'no-padding': col.name === 'address' || col.name === 'avg_rent',
                'text-weight-medium': subjectDetail && props.row.id === subjectDetail.id,
                'bg-dark':
                  col.name === 'actions' && $q.dark.isActive && props.row.id !== subjectDetail?.id,
                'bg-white':
                  col.name === 'actions' && !$q.dark.isActive && props.row.id !== subjectDetail?.id
              }"
              :colspan="col.name === 'avg_rent' ? 4 : 1"
              :props="props"
            >
              <div
                v-if="col.name === 'address'"
                :style="`min-width: ${$q.screen.lt.md ? '215' : '390'}px`"
                class="flex no-wrap no-scroll"
              >
                <div
                  v-if="$q.screen.gt.sm"
                  :style="{
                    'background-color': subjectDetail?.id === props.row.id ? '#6365f1' : '#E97F08',
                    left: deleteEnabled ? '25px' : ''
                  }"
                  class="absolute q-ma-xs text-white text-bold"
                  style="border-radius: 50%; width: 24px; height: 24px; z-index: 1"
                >
                  <div class="flex justify-center items-center" style="height: 100%">
                    <i
                      v-if="subjectDetail?.id === props.row.id"
                      class="material-icons"
                      style="font-size: 14px"
                      >star</i
                    >
                    <span v-else>
                      {{
                        (
                          filteredProperties.findIndex(({ prop }) => prop.id === props.row.id) +
                          (subjectVisible ? 0 : 1)
                        ).toString()
                      }}
                    </span>
                  </div>
                </div>
                <div :style="deleteEnabled ? 'padding-left: 24px' : ''" style="height: 114px">
                  <div
                    v-if="deleteEnabled"
                    class="drag-handle"
                    @mousedown.stop="startDrag(props.rowIndex)"
                  >
                    <q-icon
                      v-if="!(subjectDetail && props.row.id === subjectDetail.id)"
                      class="cursor-move text-grey-6"
                      name="drag_indicator"
                      size="20px"
                    />
                  </div>
                  <img
                    v-if="$q.screen.gt.sm && !streetviewErrors[props.row.id]"
                    :alt="'Google Street View for ' + props.row.street_address"
                    :src="getStreetViewUrlUnion(props.row)"
                    crossorigin="anonymous"
                    data-private
                    height="114px"
                    width="175px"
                    @error="streetviewErrors[props.row.id] = true"
                  />
                  <div
                    v-else-if="$q.screen.gt.sm"
                    :class="{ 'bg-grey-9': $q.dark.isActive }"
                    class="bg-grey-3 column justify-center"
                  >
                    <div
                      style="width: 175px; height: 114px; line-height: 114px; text-align: center"
                    >
                      <q-icon class="text-grey-5" name="apartment" size="lg" />
                    </div>
                  </div>
                  <div v-if="$q.screen.gt.sm" class="property-tags-container">
                    <div class="q-gutter-xs property-tags">
                      <q-badge v-if="props.row.is_affordable" class="justify-center" color="orange">
                        Affordable
                        <q-tooltip>
                          Detected Affordable Housing based on the LIHTC database and floorplan
                          names.
                        </q-tooltip>
                      </q-badge>
                      <q-badge
                        v-if="props.row.is_build_to_rent"
                        class="justify-center"
                        color="orange"
                      >
                        Build to Rent
                        <q-tooltip>
                          Detected Build to Rent Housing based on text and data analysis on the
                          property.
                        </q-tooltip>
                      </q-badge>
                      <q-badge v-if="props.row.is_student" class="justify-end" color="orange">
                        Student
                        <q-tooltip
                          >Detected Student Housing based on text and price analysis on the
                          property</q-tooltip
                        >
                      </q-badge>
                      <q-badge v-if="props.row.is_senior" class="justify-end" color="orange">
                        Senior
                        <q-tooltip
                          >Detected Senior Housing based on text analysis on the property</q-tooltip
                        >
                      </q-badge>
                      <q-badge v-if="props.row.is_lease_up" class="justify-end" color="orange">
                        Lease Up
                      </q-badge>
                    </div>
                  </div>
                </div>
                <div class="q-px-md q-my-auto selectable-text">
                  <div class="row q-gutter-x-xs">
                    <router-link
                      v-if="hasPropertyReports && props.row.id !== subjectDetail?.id"
                      :to="{ name: 'New Property Report', query: { propertyId: props.row.id } }"
                      class="text-bold"
                      style="text-decoration: none; color: inherit"
                      target="_blank"
                    >
                      {{ props.row.building_name || props.row.street_address }}
                    </router-link>
                    <div v-else class="text-bold">
                      {{ props.row.building_name || props.row.street_address }}
                    </div>
                    <div v-if="subjectDetail && props.row.id !== subjectDetail.id">
                      ({{ haversineDist(subjectDetail, props.row).toFixed(2) }} mi)
                    </div>
                    <div v-if="props.row.user_id">
                      <q-icon class="q-mr-xs" name="lock_outline" />
                      <q-tooltip
                        >This custom property is visible exclusively to report owners and those that
                        have been granted access.</q-tooltip
                      >
                    </div>
                  </div>
                  <div>{{ props.row.street_address }}</div>
                  <div>{{ props.row.city }}, {{ props.row.state }} {{ props.row.zip_code }}</div>
                  <template v-if="props.row.management_company">
                    <q-icon
                      v-if="props.row.management_company"
                      class="q-mr-xs"
                      name="corporate_fare"
                    />
                    {{ props.row.management_company }}
                  </template>
                  <div :id="`intercom-${props.rowIndex}-insights`" class="row q-gutter-x-xs">
                    <div v-if="hasQualityScore(props.row)">
                      <q-btn
                        :flat="printingMode"
                        :outline="!printingMode"
                        color="primary"
                        dense
                        size="sm"
                      >
                        <span
                          style="
                            height: 14px;
                            text-align: center;
                            font-size: 11px;
                            line-height: 14px;
                            margin: 0px 0px 0px 2px;
                          "
                        >
                          {{
                            humanizeQualityScore(
                              props.row.building_quality.property_overall_quality
                            )
                          }}
                        </span>
                        <i v-if="!printingMode" class="material-icons hd-icon"> arrow_drop_down</i>
                      </q-btn>
                      <q-menu
                        max-width="250px"
                        transition-duration="100"
                        @show="$tracker.openedPopup('quality_breakdown', 'rent_comps')"
                      >
                        <q-card>
                          <q-card-section class="column q-gutter-xs q-pa-md">
                            <span class="text-bold">Detected Quality</span>
                            <span
                              :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'"
                              class="text-"
                            >
                              HelloData's rating on quality and condition based on the property
                              photos.
                            </span>
                          </q-card-section>
                          <q-separator />
                          <q-markup-table dense flat square>
                            <thead>
                              <tr class="text-left">
                                <th class="text-left" scope="col">Room</th>
                                <th class="text-right" scope="col">Score</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="quality in qualityRows(props.row)"
                                :key="quality.key"
                                :class="{
                                  'text-bold bg-grey-1': quality.isOverall,
                                  'bg-grey-9': $q.dark.isActive && quality.isOverall
                                }"
                              >
                                <td>{{ quality.label }}</td>
                                <td class="text-right">{{ quality.score }}/10</td>
                              </tr>
                            </tbody>
                          </q-markup-table>
                        </q-card>
                      </q-menu>
                    </div>
                    <div v-if="props.row.review_analysis">
                      <q-btn
                        :flat="printingMode"
                        :outline="!printingMode"
                        clickable
                        color="primary"
                        dense
                        size="sm"
                      >
                        <span
                          style="
                            height: 14px;
                            text-align: center;
                            font-size: 10px;
                            line-height: 14px;
                            margin: 0px 0px 0px 2px;
                          "
                        >
                          <i class="material-icons hd-icon">star</i>
                          {{ humanizeReviewScore(props.row.review_analysis?.avg_score) }}
                        </span>
                        <i v-if="!printingMode" class="material-icons hd-icon"> arrow_drop_down</i>
                      </q-btn>
                      <q-menu
                        max-width="500px"
                        transition-duration="100"
                        @show="$tracker.openedPopup('review_breakdown', 'rent_comps')"
                      >
                        <card-review-analysis :review-analysis="props.row.review_analysis" />
                      </q-menu>
                    </div>
                    <div v-if="props.row.pricing_strategy.count_prices && !printingMode">
                      <q-btn clickable color="primary" dense outline size="sm">
                        <i class="material-icons hd-icon">show_chart</i>
                        <i class="material-icons hd-icon">arrow_drop_down</i>
                      </q-btn>
                      <q-menu
                        transition-duration="100"
                        @show="$tracker.openedPopup('pricing_strategy', 'rent_comps')"
                      >
                        <card-pricing-strategy :pricing-strategy="props.row.pricing_strategy" />
                      </q-menu>
                    </div>
                    <div v-if="hasConcessions(props.row, dateRange) && !printingMode">
                      <q-btn clickable color="primary" dense outline size="sm">
                        <i class="material-icons hd-icon">attach_money</i>
                        <i class="material-icons hd-icon"> arrow_drop_down</i>
                      </q-btn>
                      <q-menu transition-duration="100" @show="showConcessionsPopup">
                        <q-banner class="no-padding block">
                          <concessions-card :date-range="dateRange" :property="props.row" />
                        </q-banner>
                      </q-menu>
                    </div>
                    <a
                      v-if="cleanURL(props.row.building_website) && !printingMode"
                      :href="cleanURL(props.row.building_website)!"
                      style="text-decoration: none"
                      target="_blank"
                    >
                      <q-btn clickable color="primary" dense outline size="sm">
                        <i class="material-icons hd-icon">link</i>
                        <i class="material-icons hd-icon" style="margin: 0px 1px 0px 1px"
                          >open_in_new</i
                        >
                      </q-btn>
                    </a>
                    <div v-if="!printingMode">
                      <q-btn
                        clickable
                        color="primary"
                        dense
                        outline
                        size="sm"
                        @click="fetchOwnerDataForProperty(props.row)"
                      >
                        <i class="material-icons hd-icon">person</i>
                        <i class="material-icons hd-icon">arrow_drop_down</i>
                      </q-btn>
                      <q-menu style="max-width: 400px" transition-duration="100">
                        <q-card>
                          <q-card-section>
                            <div class="text-bold">Property Ownership</div>
                          </q-card-section>
                          <q-separator />
                          <template v-if="taxAssessorOwnerByProperty[props.row.id]">
                            <q-card-section
                              v-if="isLoading(taxAssessorOwnerByProperty[props.row.id])"
                              class="row justify-center"
                            >
                              <q-spinner color="primary" size="sm" />
                            </q-card-section>
                            <q-card-section
                              v-else-if="isError(taxAssessorOwnerByProperty[props.row.id])"
                              class="column items-center justify-center q-gutter-y-sm q-py-lg"
                            >
                              <q-icon
                                :color="$q.dark.isActive ? 'orange-4' : 'orange-6'"
                                name="warning"
                                size="md"
                              />
                              <div class="text-center">
                                <div class="text-subtitle2 q-mb-xs">
                                  Unable to Load Ownership Data
                                </div>
                                <div
                                  :class="$q.dark.isActive ? 'text-grey-4' : 'text-grey-6'"
                                  class="text-caption"
                                >
                                  There was an issue retrieving the ownership information for this
                                  property. Please try again later.
                                </div>
                              </div>
                            </q-card-section>
                            <q-card-section
                              v-else-if="
                                isLoaded(taxAssessorOwnerByProperty[props.row.id]) &&
                                taxAssessorOwnerByProperty[props.row.id].data.tax_assessor_owner_v2
                                  .length === 0
                              "
                            >
                              <div class="text-grey-7">No Ownership Data Available</div>
                            </q-card-section>
                            <template
                              v-else-if="isLoaded(taxAssessorOwnerByProperty[props.row.id])"
                            >
                              <template
                                v-for="(owner, index) in taxAssessorOwnerByProperty[props.row.id]
                                  .data.tax_assessor_owner_v2"
                                :key="owner.tax_assessor_id"
                              >
                                <q-card-section>
                                  <div class="row items-center">
                                    <div class="text-weight-medium">
                                      {{ formatOwnerName(owner) }}
                                    </div>
                                    <q-badge
                                      v-if="owner.is_owner_company"
                                      :class="$q.dark.isActive ? 'text-white' : 'text-grey-8'"
                                      class="q-ml-sm"
                                      outline
                                    >
                                      <q-icon class="q-mr-xs" name="business" size="xs" />
                                      Company
                                    </q-badge>
                                    <q-badge
                                      v-else
                                      :class="$q.dark.isActive ? 'text-white' : 'text-grey-8'"
                                      class="q-ml-sm"
                                      outline
                                    >
                                      <q-icon class="q-mr-xs" name="person" size="xs" />
                                      Individual
                                    </q-badge>
                                  </div>
                                  <div
                                    v-if="
                                      owner.owner_name_latest_deed &&
                                      owner.owner_name_latest_deed !== formatOwnerName(owner)
                                    "
                                    class="q-mt-xs"
                                  >
                                    Latest Deed:
                                    <span class="text-grey-7">{{
                                      toTitleCase(owner.owner_name_latest_deed)
                                    }}</span>
                                  </div>
                                  <div v-if="owner.owner_trust_type_code" class="q-mt-xs">
                                    Trust Type:
                                    <span class="text-grey-7">{{
                                      toTitleCase(owner.owner_trust_type_code)
                                    }}</span>
                                  </div>
                                </q-card-section>
                                <q-separator
                                  v-if="
                                    index <
                                    taxAssessorOwnerByProperty[props.row.id].data
                                      .tax_assessor_owner_v2.length -
                                      1
                                  "
                                />
                              </template>
                            </template>
                          </template>
                          <q-card-section v-else>
                            <div class="text-grey-7">No Ownership Data Available</div>
                          </q-card-section>
                        </q-card>
                      </q-menu>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="col.name === 'similarity'">
                <template v-if="props.row.id !== subjectDetail?.id">
                  <div
                    v-if="
                      props.row.id in similarities && similarities[props.row.id].overall !== null
                    "
                  >
                    <q-knob
                      :max="1"
                      :min="0"
                      :model-value="similarities[props.row.id].overall!"
                      class="cursor-pointer"
                      color="green"
                      instant-feedback
                      readonly
                      show-value
                    >
                      <div>
                        {{ formatPercent(similarities[props.row.id].overall, 0) }}
                      </div>
                    </q-knob>
                    <q-menu
                      transition-duration="100"
                      @show="$tracker.openedPopup('similarity_breakdown', 'rent_comps')"
                    >
                      <q-card flat style="max-width: 300px">
                        <similarity-card
                          :hidden-dimensions="
                            subjectDetail?.is_student ? [] : ['distances_to_universities']
                          "
                          :similarities="similarities[props.row.id]"
                        />
                      </q-card>
                    </q-menu>
                  </div>
                  <q-skeleton v-else animation="blink" class="q-mx-auto" type="circle" />
                </template>
                <template v-else-if="subjectDetail && props.row.id !== subjectDetail.id">
                  <q-skeleton animation="blink" class="q-mx-auto" type="circle" />
                </template>
              </div>
              <div v-else-if="col.name === 'estimated_occupancy'">
                <div v-if="col.value">
                  {{ formatPercent(col.value.last.advertisedOccupancy) }}
                  <q-tooltip max-width="250px">
                    Estimated by dividing {{ col.value.last.advertisedUnits }}, the number of units
                    marketed as available within the next 7 days, by
                    {{ col.value.last.encounteredUnits }},
                    {{
                      props.row.number_units === col.value.last.encounteredUnits
                        ? 'the total number of units in the building.'
                        : 'the total number of units we encountered to date for this development.'
                    }}
                  </q-tooltip>
                </div>
                <div
                  v-if="col.value?.last.advertisedOccupancy && col.value?.first.advertisedOccupancy"
                >
                  <q-badge
                    :color="
                      col.value.last.advertisedOccupancy > col.value.first.advertisedOccupancy
                        ? 'positive'
                        : col.value.last.advertisedOccupancy < col.value.first.advertisedOccupancy
                          ? 'negative'
                          : 'grey'
                    "
                    dense
                    outline
                  >
                    {{
                      formatPercent(
                        col.value.last.advertisedOccupancy - col.value.first.advertisedOccupancy,
                        1,
                        true
                      )
                    }}
                  </q-badge>
                  <q-tooltip max-width="250px">
                    <p class="q-mb-xs">
                      Advertised Leased % at this property has
                      {{
                        col.value.last.advertisedOccupancy > col.value.first.advertisedOccupancy
                          ? 'increased'
                          : 'decreased'
                      }}
                      by
                      {{
                        formatPercent(
                          col.value.last.advertisedOccupancy - col.value.first.advertisedOccupancy,
                          1,
                          true
                        )
                      }}
                      compared to
                      {{ differenceInDays(col.value.last.asOf, col.value.first.asOf) }}-days ago.
                    </p>
                    <p class="q-mb-none">
                      As of {{ format(col.value.last.asOf, 'MMMM do') }}, there were
                      {{ col.value.last.advertisedUnits }} units listed as available for move in,
                      implying they are vacant, representing an advertised leased % of
                      {{ formatPercent(col.value.last.advertisedOccupancy, 1) }} of the
                      {{ props.row.number_units }} units in the property.
                    </p>
                    <p class="q-mb-none">
                      {{ differenceInDays(col.value.last.asOf, col.value.first.asOf) }} days ago on
                      {{ format(col.value.first.asOf, 'MMMM do') }}, there were
                      {{ col.value.first.advertisedUnits }} units listed as available for move in,
                      representing an advertised leased % of
                      {{ formatPercent(col.value.first.advertisedOccupancy, 1) }}.
                    </p>
                  </q-tooltip>
                </div>
              </div>
              <div v-else-if="col.name === 'exposure_percent'">
                <div v-if="col.value">
                  {{ formatPercent(col.value.last.exposure) }}
                  <q-tooltip max-width="250px">
                    Estimated by dividing {{ col.value.last.exposedUnits }}, the number of units
                    marketed as available within the next 6 months, by
                    {{ col.value.last.encounteredUnits }},
                    {{
                      props.row.number_units === col.value.last.encounteredUnits
                        ? 'the total number of units in the building.'
                        : 'the total number of units we have encountered to date for this development.'
                    }}.
                  </q-tooltip>
                </div>
                <div v-if="col.value">
                  <q-badge
                    :color="
                      col.value.last.exposure > col.value.first.exposure
                        ? 'negative'
                        : col.value.last.exposure < col.value.first.exposure
                          ? 'positive'
                          : 'grey'
                    "
                    dense
                    outline
                  >
                    {{ formatPercent(col.value.last.exposure - col.value.first.exposure, 1, true) }}
                  </q-badge>
                  <q-tooltip max-width="250px">
                    <p class="q-mb-xs">
                      Exposure at this property has
                      {{
                        col.value.last.exposure > col.value.first.exposure
                          ? 'increased'
                          : 'decreased'
                      }}
                      by
                      {{
                        formatPercent(col.value.last.exposure - col.value.first.exposure, 1, true)
                      }}
                      compared to
                      {{ differenceInDays(col.value.last.asOf, col.value.first.asOf) }} days ago.
                    </p>
                    <p class="q-mb-none">
                      As of {{ format(col.value.last.asOf, 'MMMM do') }}, there were
                      {{ col.value.last.exposedUnits }} units listed as available within the next 6
                      months, representing an exposure of
                      {{ formatPercent(col.value.last.exposure, 1) }} of the
                      {{ props.row.number_units }} units in the property.
                    </p>
                    <p class="q-mb-none">
                      {{ differenceInDays(col.value.last.asOf, col.value.first.asOf) }} days ago on
                      {{ format(col.value.first.asOf, 'MMMM do') }}, there were
                      {{ col.value.first.exposedUnits }} units listed as available within the next 6
                      months, representing an exposure of
                      {{ formatPercent(col.value.first.exposure, 1) }}.
                    </p>
                  </q-tooltip>
                </div>
              </div>
              <div v-else-if="col.name === 'number_units'">
                <edit-anything
                  :disabled="!deleteEnabled"
                  :rules="[checkNumbers(1, 10000)]"
                  :value="col.value.number_units"
                  data-type="integer"
                  tooltip="Edit Number Units"
                  @input="updatePropertyDetails(props.row.id, 'number_units', $event)"
                >
                  <span :class="{ 'predicted-number cursor-pointer': col.value.predicted }">
                    {{ col.value.number_units }}
                    <q-tooltip v-if="col.value.predicted && !deleteEnabled">{{
                      col.value.description
                    }}</q-tooltip>
                  </span>
                </edit-anything>
              </div>
              <div v-else-if="col.name === 'year_built'">
                <edit-anything
                  :disabled="!deleteEnabled"
                  :rules="[checkNumbers(1700, 2025)]"
                  :value="col.value.year_built"
                  data-type="integer"
                  tooltip="Edit Year Built"
                  @input="updatePropertyDetails(props.row.id, 'year_built', $event)"
                >
                  <span :class="{ 'predicted-number cursor-pointer': col.value.predicted }">
                    {{ col.value.year_built }}
                    <q-tooltip v-if="col.value.predicted">{{ col.value.description }}</q-tooltip>
                  </span>
                </edit-anything>
              </div>
              <div v-else-if="col.name === 'number_stories'">
                <edit-anything
                  :disabled="!deleteEnabled"
                  :rules="[checkNumbers(1, 110)]"
                  :value="col.value.number_stories"
                  data-type="integer"
                  tooltip="Edit Number Stories"
                  @input="updatePropertyDetails(props.row.id, 'number_stories', $event)"
                >
                  <span :class="{ 'predicted-number cursor-pointer': col.value.predicted }">
                    {{ col.value.number_stories }}
                  </span>
                  <q-tooltip v-if="col.value.predicted && !deleteEnabled">{{
                    col.value.description
                  }}</q-tooltip>
                </edit-anything>
              </div>
              <div v-else-if="col.name === 'avg_rent'">
                <q-markup-table class="bg-transparent" dense flat square wrap-cells>
                  <tbody>
                    <tr
                      v-for="(x, idx) in computeAverages(props.row)"
                      :key="idx"
                      :class="{
                        'bg-deep-purple-light':
                          hoveredUnitTypeIdx === idx && props.row.id !== subjectDetail?.id
                      }"
                      class="text-right"
                      @mouseleave="
                        subjectDetail && props.row.id === subjectDetail.id
                          ? (hoveredUnitTypeIdx = -1)
                          : null
                      "
                      @mouseover="
                        subjectDetail && props.row.id === subjectDetail.id
                          ? (hoveredUnitTypeIdx = idx)
                          : null
                      "
                    >
                      <td class="text-left" style="width: 25%">
                        {{ x.label }}
                        <q-tooltip max-width="250px"> {{ x.explanation }} </q-tooltip>
                      </td>
                      <td style="width: 25%">
                        {{ x.rent }}
                        <q-tooltip max-width="250px"> {{ x.explanation }} </q-tooltip>
                      </td>
                      <td style="width: 25%">
                        {{ x.sqft }}
                        <q-tooltip max-width="250px"> {{ x.explanation }} </q-tooltip>
                      </td>
                      <td style="width: 25%">
                        {{ x.psf }}
                        <q-tooltip max-width="250px"> {{ x.explanation }} </q-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </q-markup-table>
              </div>
              <div v-else-if="col.name === 'actions'" class="column inline">
                <q-btn
                  v-if="reportId && reportId !== -1 && props.row.user_id"
                  :to="{
                    name: 'New Development',
                    query: { existingPropertyId: props.row.id, gotoReportIdNext: reportId }
                  }"
                  color="primary"
                  flat
                  size="sm"
                  square
                >
                  <q-icon class="col" color="primary" name="edit_note" />
                  <q-tooltip>Change this custom property's details</q-tooltip>
                </q-btn>
                <q-btn
                  color="primary"
                  flat
                  size="sm"
                  square
                  @click="openEditPropertyDialog(props.row)"
                >
                  <q-icon class="col" color="primary" name="edit" />
                  <q-tooltip>Edit Property Characteristics</q-tooltip>
                </q-btn>

                <q-btn
                  :id="`intercom-delete-property-${props.rowIndex}`"
                  color="red"
                  flat
                  size="sm"
                  square
                  @click="deleteAddress(props.row.id)"
                >
                  <q-icon class="col" color="red" name="delete" />
                  <q-tooltip>Do not include this property in the report</q-tooltip>
                </q-btn>
              </div>
              <div v-else>
                {{ col.value }}
              </div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
      <q-dialog
        v-if="editPropertyValue"
        v-model="editPropertyDialog"
        persistent
        transition-duration="100"
      >
        <q-card class="full-width" flat style="max-width: 800px">
          <q-card-section class="row justify-between items-start">
            <div class="col-10">
              <div class="text-h6">Edit Property Characteristics</div>
              <q-banner
                v-if="editPropertyValue?.user_id"
                class="bg-amber-1 text-black q-mb-sm"
                rounded
              >
                <template #avatar>
                  <q-icon color="amber-9" name="warning" />
                </template>
                <strong>Warning:</strong> Editing this custom development will change the
                development data for <strong>ALL</strong> reports that use this development. If
                you've duplicated this report, those duplicates will also be affected.
              </q-banner>
              <div style="font-size: 12px">
                Our system aims to source data publicly, but reliable sources aren't always
                available. Please edit information below, if needed. We recommend updating your
                property website and ILSs first, as changes made here won't receive daily updates
                after they're applied.
                <span class="text-italic">
                  Please note, updates should reflect the existing property and are not intended for
                  adding a new comp.
                </span>
              </div>
            </div>
            <q-space />
            <q-btn
              v-close-popup
              :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'"
              dense
              flat
              icon="close"
              round
            />
          </q-card-section>
          <q-tabs
            v-model="currentEditPropertyTab"
            :class="$q.dark.isActive ? 'text-grey-2' : 'text-grey-8'"
            active-color="primary"
            align="left"
            dense
            indicator-color="primary"
            inline-label
            narrow-indicator
            no-caps
          >
            <q-tab icon="list_alt" label="Info" name="info"> </q-tab>
            <q-tab icon="paid" label="Fees" name="fees"> </q-tab>
            <q-tab icon="auto_awesome" label="Amenities" name="amenities"> </q-tab>
          </q-tabs>
          <q-separator />
          <q-card-section class="q-pa-sm" style="height: 40vh; overflow-y: auto">
            <q-tab-panels v-model="currentEditPropertyTab">
              <q-tab-panel name="info">
                <div class="q-gutter-md">
                  <div class="row items-end">
                    <div class="col-12 col-md-5 column items-center items-md-end q-pr-sm">
                      <property-image
                        :alt="
                          editPropertyValue.street_view_url
                            ? `Custom Thumbnail View for ${editPropertyValue.street_address}`
                            : `Google Street View for ${editPropertyValue.street_address}`
                        "
                        :img-src="
                          editPropertyValue.street_view_url
                            ? editPropertyValue.street_view_url
                            : getStreetViewUrlStrict(editedProperty!)
                        "
                        crossorigin="anonymous"
                        editable
                        @click="uploadFileDialog = true"
                      />
                      <q-dialog
                        v-model="uploadFileDialog"
                        persistent
                        transition-hide="slide-down"
                        transition-show="slide-up"
                      >
                        <q-card flat style="width: 400px">
                          <q-card-section>
                            <div class="text-h6">Upload Property Thumbnail</div>
                          </q-card-section>
                          <q-separator />
                          <q-card-section class="text-caption">
                            <q-scroll-area
                              ref="scrollAreaRef"
                              class="relative-position"
                              style="height: 200px"
                              @scroll="handleScroll"
                            >
                              <div
                                v-if="!hasScrolledUploadTerms"
                                :style="
                                  $q.dark.isActive
                                    ? 'background: linear-gradient(transparent, #1d1d1d 70%)'
                                    : 'background: linear-gradient(transparent, white 70%)'
                                "
                                class="text-center full-width q-pb-md"
                                style="position: absolute; top: 150px"
                              >
                                <q-icon
                                  class="cursor-pointer"
                                  color="primary"
                                  name="expand_circle_down"
                                  size="md"
                                  @click="scrollDown"
                                >
                                </q-icon>
                              </div>
                              <p>
                                By uploading photos, you agree to the following terms and
                                conditions:
                              </p>
                              <p>
                                <b>Ownership and Rights:</b> You affirm that you are the owner of
                                the uploaded photo(s) or have obtained all necessary rights and
                                permissions from the owner to use and upload the photo(s) to this
                                platform. You guarantee that the uploaded photo(s) do not infringe
                                on any third party's rights, including but not limited to copyright,
                                trademark, or privacy rights.
                              </p>
                              <p>
                                <b>Appropriate Content:</b> You agree to only upload photos that are
                                relevant to the property being represented. Photos that contain
                                inappropriate, unrelated, or misleading content are strictly
                                prohibited.
                              </p>
                              <p>
                                <b>Indemnification:</b> You agree to indemnify and hold Hello Data,
                                Inc. harmless from any and all claims, damages, liabilities, costs,
                                and expenses (including reasonable attorney's fees) arising from or
                                related to:
                              </p>
                              <ul>
                                <li>
                                  Your violation of any third party rights, , including but not
                                  limited to copyright, trademark, or privacy rights.
                                </li>
                                <li>
                                  Any claims that the uploaded photo(s) are inappropriate,
                                  misleading, or irrelevant.
                                </li>
                                <li>Any misuse of the uploaded photo(s) by third parties.</li>
                              </ul>
                              <p>
                                <b>Liability:</b> Hello Data, Inc. is not responsible for verifying
                                the ownership or appropriateness of the uploaded photo(s). You
                                acknowledge that Hello Data, Inc. will not be liable for any issues
                                arising from your use of the photo upload feature, including any
                                disputes over rights or inappropriate content.
                              </p>
                              <p>
                                <b>Removal of Photos:</b> Hello Data, Inc. reserves the right to
                                remove any uploaded photo(s) at its sole discretion, without prior
                                notice, if the photo(s) are found to violate these terms or for any
                                other reason deemed appropriate by Hello Data, Inc.
                              </p>
                              <p>
                                By clicking "Upload" and uploading your photo(s), you confirm that
                                you have read, understood, and agree to these terms and conditions.
                              </p>
                            </q-scroll-area>
                          </q-card-section>
                          <q-separator />
                          <q-card-section>
                            <q-img
                              v-if="tempImageFile"
                              :src="tempImageFile"
                              class="q-mb-sm"
                              fill="cover"
                              height="200px"
                              width="300px"
                            />
                            <q-file
                              v-model="imageFile"
                              :accept="acceptedImageExtensionsFiletypes"
                              :loading="compressingImage"
                              :max-file-size="5 * 1024 * 1024"
                              bottom-slots
                              clearable
                              counter
                              filled
                              label="Choose a file"
                              @rejected="onRejected"
                              @update:model-value="onUpdateModelValue"
                            >
                              <template #hint>
                                Accepted file types: {{ acceptedImageExtensionsFiletypes }} (max
                                5MB)
                              </template>
                            </q-file>
                          </q-card-section>
                          <q-separator />
                          <q-card-actions align="right">
                            <q-btn flat label="Cancel" no-caps @click="uploadFileDialog = false" />
                            <q-btn
                              color="primary"
                              label="Upload"
                              no-caps
                              type="submit"
                              unelevated
                              @click="uploadFile"
                            />
                          </q-card-actions>
                        </q-card>
                      </q-dialog>
                    </div>
                    <div class="col-12 col-md q-gutter-y-sm q-pt-sm">
                      <q-btn
                        v-if="editPropertyValue.street_view_url"
                        :class="{ 'text-red-2': $q.dark.isActive }"
                        class="q-mt-sm q-mr-sm text-red-8"
                        dense
                        label="Reset Thumbnail"
                        no-caps
                        unelevated
                        @click="resetImage"
                      />
                      <q-input
                        v-model="editPropertyValue.street_address"
                        dense
                        label="Street Address"
                        outlined
                      />
                      <q-input
                        v-model="editPropertyValue.building_name"
                        dense
                        label="Building Name"
                        outlined
                      />
                      <q-input
                        v-model="editPropertyValue.management_company"
                        dense
                        hide-bottom-space
                        label="Management Company"
                        outlined
                      />
                      <q-input
                        v-model="editPropertyValue.building_website"
                        :rules="[(val) => isValidURL(val) || 'Invalid URL']"
                        dense
                        hide-bottom-space
                        label="Property Website"
                        lazy-rules
                        outlined
                      />
                    </div>
                  </div>
                  <div class="q-gutter-y-sm q-mt-none">
                    <div class="row column-md q-gutter-sm q-mt-none">
                      <q-input
                        v-model.number="editPropertyValue.year_built"
                        :rules="[checkNumbers(1700, 2025)]"
                        class="col"
                        dense
                        hide-bottom-space
                        label="Year Built"
                        lazy-rules
                        outlined
                        type="number"
                      />
                      <q-input
                        v-model.number="editPropertyValue.number_units"
                        :rules="[checkNumbers(1, 10000)]"
                        class="col"
                        dense
                        hide-bottom-space
                        label="# Units"
                        lazy-rules
                        outlined
                        type="number"
                      />
                      <q-input
                        v-model.number="editPropertyValue.number_stories"
                        :rules="[checkNumbers(1, 110)]"
                        class="col"
                        dense
                        hide-bottom-space
                        label="Stories"
                        lazy-rules
                        outlined
                        type="number"
                      />
                    </div>
                  </div>
                  <q-card
                    :style="`border: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, .6)' : 'rgba(0, 0, 0, 0.25)'}`"
                    bordered
                    class="q-mt-sm property-type-box"
                    flat
                  >
                    <label
                      :class="{ 'text-grey-8': !$q.dark.isActive, 'text-grey-5': $q.dark.isActive }"
                      class="q-my-none no-pointer-events"
                    >
                      Property Tags
                    </label>
                    <div class="row q-gutter-sm font-weight-thin">
                      <q-toggle
                        v-model="editPropertyValue.is_affordable"
                        color="orange"
                        dense
                        label="Affordable"
                      />
                      <q-toggle
                        v-model="editPropertyValue.is_senior"
                        color="orange"
                        dense
                        label="Senior"
                      />
                      <q-toggle
                        v-model="editPropertyValue.is_student"
                        color="orange"
                        dense
                        label="Student"
                      />
                      <q-toggle
                        v-model="editPropertyValue.is_build_to_rent"
                        color="orange"
                        dense
                        label="Build to Rent"
                      />
                      <q-toggle
                        v-model="editPropertyValue.is_lease_up"
                        color="orange"
                        dense
                        label="Lease up"
                      />
                    </div>
                  </q-card>
                </div>
              </q-tab-panel>
              <q-tab-panel class="col-12 q-gutter-md" name="fees">
                <div class="col-6 q-gutter-y-sm">
                  <div class="text-h6">Required Fees</div>
                  <div class="row q-gutter-sm">
                    <q-input
                      v-model="editPropertyValue.admin_fee"
                      class="col-12 col-md-3"
                      dense
                      hide-bottom-space
                      label="Admin Fee"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.admin_fee = Math.round(editPropertyValue.admin_fee || 0)
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.amenity_fee"
                      class="col-12 col-md-3"
                      dense
                      label="Amenity Fee"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.amenity_fee = Math.round(
                          editPropertyValue.amenity_fee || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.application_fee"
                      class="col-12 col-md-3"
                      dense
                      label="Application Fee"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.application_fee = Math.round(
                          editPropertyValue.application_fee || 0
                        )
                      "
                    />
                  </div>
                  <div class="row items-center q-gutter-sm">
                    <div class="col col-md-3">
                      <div class="text-weight-bold q-my-auto">More Fees</div>
                    </div>
                    <div class="col col-md-3"></div>
                    <div class="col col-md-3 flex justify-end">
                      <q-btn dense flat icon="add" label="New Fee" no-caps @click="createNewFee" />
                    </div>
                  </div>
                  <div v-if="editPropertyValue.fees?.length === 0" class="row q-gutter-sm">
                    <div class="col col-md-1"></div>
                    <div class="col col-md-7 text-center">
                      Add additional, property-specific fees here.
                    </div>
                    <div class="col col-md-1"></div>
                  </div>
                  <div
                    v-for="(fee, feeIndex) in editPropertyValue.fees"
                    v-else
                    :key="feeIndex"
                    class="row q-gutter-sm"
                  >
                    <div class="col col-md-3">
                      <q-input
                        v-model="fee.label"
                        dense
                        label="Label"
                        outlined
                        @blur="fee.label = fee.label || `Fee #${feeIndex + 1}`"
                      />
                    </div>
                    <div class="col col-md-3">
                      <q-input
                        v-model="fee.amount"
                        dense
                        label="Amount"
                        outlined
                        type="number"
                        @blur="fee.amount = fee.amount || 0"
                      />
                    </div>
                    <div class="col col-md-3">
                      <q-select
                        v-model="fee.frequency"
                        :options="[
                          { label: 'One-Time', value: 'once' },
                          { label: 'Monthly', value: 'month' },
                          { label: 'Annual', value: 'year' }
                        ]"
                        class="col-10"
                        dense
                        emit-value
                        label="Frequency"
                        map-options
                        option-label="label"
                        option-value="value"
                        outlined
                      >
                      </q-select>
                    </div>
                    <q-btn
                      color="red"
                      dense
                      flat
                      icon="delete"
                      size="sm"
                      @click="deleteFeeByIndex(feeIndex)"
                    />
                  </div>
                </div>
                <div class="col-6 q-gutter-y-sm">
                  <div class="text-h6">Pet Fees</div>
                  <div class="row q-gutter-sm">
                    <q-input
                      v-model="editPropertyValue.dogs_monthly_rent"
                      class="col-12 col-md-3"
                      dense
                      label="Dog Rent"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.dogs_monthly_rent = Math.round(
                          editPropertyValue.dogs_monthly_rent || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.dogs_one_time_fee"
                      class="col-12 col-md-3"
                      dense
                      label="Dog Fee"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.dogs_one_time_fee = Math.round(
                          editPropertyValue.dogs_one_time_fee || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.dogs_deposit"
                      class="col-12 col-md-3"
                      dense
                      label="Dog Deposit"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.dogs_deposit = Math.round(
                          editPropertyValue.dogs_deposit || 0
                        )
                      "
                    />
                  </div>
                  <div class="row q-gutter-sm">
                    <q-input
                      v-model="editPropertyValue.cats_monthly_rent"
                      class="col-12 col-md-3"
                      dense
                      label="Cat Rent"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.cats_monthly_rent = Math.round(
                          editPropertyValue.cats_monthly_rent || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.cats_one_time_fee"
                      class="col-12 col-md-3"
                      dense
                      label="Cat Fee"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.cats_one_time_fee = Math.round(
                          editPropertyValue.cats_one_time_fee || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.cats_deposit"
                      class="col-12 col-md-3"
                      dense
                      label="Cat Deposit"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.cats_deposit = Math.round(
                          editPropertyValue.cats_deposit || 0
                        )
                      "
                    />
                  </div>
                </div>
                <div class="col-6 q-gutter-y-sm">
                  <div class="text-h6">Additional Fees</div>
                  <div class="row q-gutter-sm">
                    <q-input
                      v-model="editPropertyValue.storage_fee"
                      class="col-12 col-md-3"
                      dense
                      label="Storage Fee"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.storage_fee = Math.round(
                          editPropertyValue.storage_fee || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.parking_covered"
                      class="col-12 col-md-3"
                      dense
                      label="Covered Parking"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.parking_covered = Math.round(
                          editPropertyValue.parking_covered || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.parking_garage"
                      class="col-12 col-md-3"
                      dense
                      label="Garage Parking"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.parking_garage = Math.round(
                          editPropertyValue.parking_garage || 0
                        )
                      "
                    />
                    <q-input
                      v-model="editPropertyValue.parking_surface_lot"
                      class="col-12 col-md-3"
                      dense
                      label="Surface Lot Parking"
                      outlined
                      type="number"
                      @blur="
                        editPropertyValue.parking_surface_lot = Math.round(
                          editPropertyValue.parking_surface_lot || 0
                        )
                      "
                    />
                  </div>
                </div>
              </q-tab-panel>
              <q-tab-panel class="col-12 q-gutter-lg" name="amenities">
                <div class="col-12">
                  <div class="text-h6 q-mb-sm">Building Amenities</div>
                  <div v-if="editPropertyValue?.building_amenities">
                    <q-chip
                      v-for="amenity in editPropertyValue.building_amenities"
                      :key="amenity"
                      :clickable="false"
                      :color="$q.dark.isActive ? 'grey-2' : 'grey-8'"
                      :label="formatAmenity(amenity)"
                      dense
                      outline
                      removable
                      @remove="
                        editPropertyValue.building_amenities =
                          editPropertyValue.building_amenities.filter((x) => x !== amenity)
                      "
                    />
                  </div>
                  <q-select
                    v-model="buildingAmenitiesInput"
                    :option-label="formatAmenity"
                    :options="
                      buildingAmenitiesOptions ? buildingAmenitiesOptions : newBuildingAmenities
                    "
                    class="q-mt-md"
                    dense
                    input-debounce="0"
                    label="Add Building Amenity"
                    outlined
                    use-input
                    @filter="filterBuildingAmenities"
                    @update:model-value="addBuildingAmenity"
                  />
                </div>
                <div class="col-12">
                  <div class="text-h6 q-mb-sm">Unit Amenities</div>
                  <div v-if="editPropertyValue?.unit_amenities">
                    <q-chip
                      v-for="amenity in editPropertyValue.unit_amenities"
                      :key="amenity"
                      :clickable="false"
                      :color="$q.dark.isActive ? 'grey-2' : 'grey-8'"
                      :label="formatAmenity(amenity)"
                      dense
                      outline
                      removable
                      @remove="
                        editPropertyValue.unit_amenities = editPropertyValue.unit_amenities.filter(
                          (x) => x !== amenity
                        )
                      "
                    />
                  </div>
                  <q-select
                    v-model="unitAmenitiesInput"
                    :option-label="formatAmenity"
                    :options="unitAmenitiesOptions ? unitAmenitiesOptions : newUnitAmenities"
                    class="q-mt-md"
                    dense
                    input-debounce="0"
                    label="Add Unit Amenity"
                    outlined
                    use-input
                    @filter="filterUnitAmenities"
                    @update:model-value="addUnitAmenity"
                  />
                </div>
              </q-tab-panel>
            </q-tab-panels>
          </q-card-section>
          <q-separator />
          <q-separator />
          <q-card-actions align="right">
            <q-btn flat label="Cancel" no-caps @click="editPropertyDialog = false" />
            <q-btn
              color="primary"
              label="Confirm"
              no-caps
              type="submit"
              unelevated
              @click="confirmEditProperty"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </property-report-card>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QScrollArea } from 'quasar'
import type { QTableColumn } from 'quasar'
import { differenceInDays, format, parseISO } from 'date-fns'

import { useStripeStore } from '@/stores/stripe'

import {
  type Availability,
  type AvailabilityPeriod,
  avgPsf,
  BuildingAmenity,
  Column,
  getStreetViewUrl,
  haversineDist,
  humanizeQualityScore,
  humanizeReviewScore,
  humanizeString,
  type Occupancy,
  occupancyOverTime,
  type Price,
  type PropertyDetailsResponse,
  type PropertyEdits,
  type Similarities,
  type TaxAssessorOwnerV2,
  UnitAmenity
} from 'common'
import imageCompression from 'browser-image-compression'

import {
  avg,
  filterColumns,
  formatMoney,
  formatNumber,
  formatPercent,
  hasConcessions,
  type ColumnData,
  sortColumns,
  toTitleCase
} from '@/utils'
import ConcessionsCard from '@/components/PropertyReports/ConcessionsCard.vue'
import SimilarityCard from '@/components/SimilarityCard.vue'
import CardPricingStrategy from '@/components/CardPricingStrategy.vue'
import CardReviewAnalysis from '@/components/CardReviewAnalysis.vue'
import EditAnything from '@/components/EditAnything.vue'
import PropertyImage from '@/components/PropertyReports/PropertyImage.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import { isError, isLoaded, isLoading } from '@/utils/loading.ts'
import { usePropertyReportStore } from '@/stores'

export default {
  name: 'PropertyCompareBuildingInfo',
  components: {
    PropertyReportCard,
    PropertyImage,
    CardPricingStrategy,
    CardReviewAnalysis,
    ConcessionsCard,
    EditAnything,
    SimilarityCard
  },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    similarities: {
      type: Object as PropType<{ [key: string]: Similarities }>,
      default: null
    },
    deleteEnabled: {
      type: Boolean,
      default: false
    },
    skeletonRows: {
      type: Number,
      default: 5
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    reportId: {
      type: Number as PropType<number | null>,
      default: null
    },
    visibleColumns: {
      type: Array as PropType<Column[]>,
      required: true
    },
    toggleableColumns: {
      type: Object as PropType<ColumnData>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['delete-address', 'add-note', 'move-comp', 'update-property-details'],
  data() {
    return {
      Column,
      buildingAmenitiesInput: null,
      unitAmenitiesInput: null,
      buildingAmenitiesCache: [] as { original: BuildingAmenity; searchStr: string }[],
      unitAmenitiesCache: [] as { original: UnitAmenity; searchStr: string }[],
      streetviewErrors: {} as { [key: string]: boolean },
      hoveredUnitTypeIdx: -1,
      metricSelection: { label: 'Asking Rent', value: 'rent' },
      metricOptions: [
        { label: 'Asking Rent', value: 'rent' },
        { label: 'Effective Rent', value: 'effective_rent' }
      ],
      editPropertyDialog: false,
      editPropertyValue: null as Partial<PropertyEdits> | null,
      editedProperty: null as PropertyDetailsResponse | null,
      imageFile: null as File | null,
      tempImageFile: null as string | null,
      uploadFileDialog: false,
      hasScrolledUploadTerms: false,
      currentEditPropertyTab: 'info' as 'info' | 'fees' | 'amenities',
      buildingAmenitiesOptions: undefined as BuildingAmenity[] | undefined,
      unitAmenitiesOptions: undefined as UnitAmenity[] | undefined,
      draggedOverIndex: -1,
      currentDragIndex: -1,
      acceptedImageExtensions: ['jpg', 'png', 'bmp', 'jpeg', 'webp'],
      compressingImage: false
    }
  },
  computed: {
    acceptedImageExtensionsFiletypes() {
      return this.acceptedImageExtensions.map((ext) => `.${ext}`).join(', ')
    },
    subjectVisible() {
      return this.filteredProperties.some((_) => _.prop.id === this.subjectDetail?.id)
    },
    allBuildingAmenities() {
      return Object.values(BuildingAmenity)
    },
    allUnitAmenities() {
      return Object.values(UnitAmenity)
    },
    newBuildingAmenities() {
      const editPropertyValue = this.editPropertyValue
      if (!editPropertyValue) {
        return []
      }
      return this.allBuildingAmenities.filter(
        (x) => !(editPropertyValue.building_amenities ?? []).includes(x)
      )
    },
    newUnitAmenities() {
      const editPropertyValue = this.editPropertyValue
      if (!editPropertyValue) {
        return []
      }
      return this.allUnitAmenities.filter(
        (x) => !(editPropertyValue.unit_amenities ?? []).includes(x)
      )
    },
    user() {
      return this.$auth0.user?.value
    },
    occupancies() {
      // build a dictionary of property id to occupancy over time
      return this.filteredProperties.reduce(
        (acc, { prop }) => {
          acc[prop.id] = occupancyOverTime(prop, this.dateRange.from, this.dateRange.to)
          return acc
        },
        {} as { [key: string]: Occupancy[] }
      )
    },
    hasPropertyReports() {
      return useStripeStore().hasPropertyReports
    },
    qualities(): { [key: string]: number } {
      const res: { [key: string]: number } = {}
      this.filteredProperties.forEach(({ prop }) => {
        const stars = this.computeStars(prop)
        if (stars !== null) {
          res[prop.id] = stars
        }
      })
      return res
    },
    subjectBeds() {
      const subject = this.filteredProperties.find((_) => _.prop.id === this.subjectDetail?.id)
      return new Set(subject?.units.map(({ unit }) => unit.bed) || [])
    },
    skeletonStyles() {
      if (this.deleteEnabled) {
        return {
          actions: 'width: 4.7%',
          stories: 'width: 6.1%',
          number_units: 'width: 6.15%',
          year_built: 'width: 7.3%',
          exposure_percent: 'width: 8.2%',
          estimated_occupancy: 'width: 7.2%',
          similarity: 'width: 7.25%',
          address: 'width: 36.2%'
        }
      } else {
        return {
          actions: 'width: 5.8%',
          stories: 'width: 6.55%',
          number_units: 'width: 6.55%',
          year_built: 'width: 7.8%',
          exposure_percent: 'width: 8.6%',
          estimated_occupancy: 'width: 7.7%',
          similarity: 'width: 7.7%',
          address: 'width: 38.6%'
        }
      }
    },
    rows() {
      // Not copying the prop object to save time
      // if some side effect happens, use {...prop} instead
      return this.filteredProperties.map((_) => _.prop)
    },
    columns(): QTableColumn[] {
      return [
        {
          field: 'address',
          name: Column.ADDRESS,
          align: 'left'
        },
        this.subjectDetail
          ? {
              field: (row: PropertyDetailsResponse) => {
                return this.similarities[row.id]?.overall || 1
              },
              name: Column.SIMILARITY,
              label: 'Similarity',
              align: 'center',
              sortable: true,
              sortOrder: 'da',
              description: this.toggleableColumns[Column.SIMILARITY].description
            }
          : null,
        {
          name: Column.ESTIMATED_OCCUPANCY,
          label: 'Leased % (Adv)',
          align: 'center',
          sortable: true,
          field: (row: PropertyDetailsResponse) => {
            const relevantOccupancy = this.occupancies[row.id]
            if (relevantOccupancy.length === 0) {
              return null
            }
            return relevantOccupancy[relevantOccupancy.length - 1].advertisedOccupancy
          },
          format: (_, row: PropertyDetailsResponse) => {
            const relevantOccupancy = this.occupancies[row.id]
            if (relevantOccupancy.length === 0) {
              return null
            }
            return {
              first: relevantOccupancy[0],
              last: relevantOccupancy[relevantOccupancy.length - 1]
            }
          },
          sortOrder: 'da',
          description: this.toggleableColumns[Column.ESTIMATED_OCCUPANCY].description
        },
        {
          field: (row: PropertyDetailsResponse) => {
            const relevantOccupancy = this.occupancies[row.id]
            if (relevantOccupancy.length === 0) {
              return null
            }
            return relevantOccupancy[relevantOccupancy.length - 1].exposure
          },
          name: Column.EXPOSURE_PERCENT,
          label: 'Exposure',
          align: 'center',
          sortable: true,
          format: (_, row: PropertyDetailsResponse) => {
            const relevantOccupancy = this.occupancies[row.id]
            if (relevantOccupancy.length === 0) {
              return null
            }
            return {
              first: relevantOccupancy[0],
              last: relevantOccupancy[relevantOccupancy.length - 1]
            }
          },
          sortOrder: 'da',
          description: this.toggleableColumns[Column.EXPOSURE_PERCENT].description
        },
        {
          field: (row: PropertyDetailsResponse) => {
            if (row.year_built) {
              return row.year_built
            }
            if (row.year_built_prediction) {
              const matches = row.year_built_prediction.match(/\d+/g)
              if (!matches) {
                return null
              }
              if (matches.length > 1) {
                return Number(matches[1])
              }
              return Number(matches[0])
            }
            return null
          },
          name: Column.YEAR_BUILT,
          label: 'Year Built',
          align: 'center',
          sortable: true,
          format: (_, row: PropertyDetailsResponse) => {
            if (row.year_built) {
              return { year_built: row.year_built, predicted: false }
            }
            if (row.year_built_prediction) {
              return {
                year_built: row.year_built_prediction,
                predicted: true,
                description: 'Predicted year built based on facade pictures'
              }
            }
            return { year_built: null, predicted: false }
          },
          sortOrder: 'da',
          description: this.toggleableColumns[Column.YEAR_BUILT].description
        },
        {
          field(row: PropertyDetailsResponse) {
            if (row.number_units) {
              return row.number_units
            }
            if (row.number_units_prediction) {
              return row.number_units_prediction
            }
            return null
          },
          name: Column.NUMBER_UNITS,
          label: '# Units',
          align: 'center',
          sortable: true,
          format: (
            _,
            row: PropertyDetailsResponse & { number_units_prediction: string | null }
          ) => {
            if (row.number_units) {
              return { number_units: row.number_units, predicted: false }
            }
            if (row.number_units_prediction !== null) {
              return {
                number_units: this.formatPredictedNumberUnits(row.number_units_prediction),
                predicted: true,
                description:
                  'Predicted number of units based on facade pictures, building characteristics, location and units that have been listed'
              }
            }
            return { number_units: null, predicted: false }
          },
          sortOrder: 'da',
          description: this.toggleableColumns[Column.NUMBER_UNITS].description
        },
        {
          field: (row: PropertyDetailsResponse) => {
            if (row.number_stories) {
              return row.number_stories
            }
            if (row.number_stories_prediction) {
              const matches = row.number_stories_prediction.match(/\d+/g)
              if (!matches) {
                return null
              }
              if (matches.length > 1) {
                return Number(matches[1])
              }
              return Number(matches[0])
            }
            return null
          },
          name: Column.NUMBER_STORIES,
          label: 'Stories',
          align: 'center',
          sortable: true,
          format: (_, row: PropertyDetailsResponse) => {
            if (row.number_stories) {
              return { number_stories: row.number_stories, predicted: false }
            }
            if (row.number_stories_prediction) {
              return {
                number_stories: row.number_stories_prediction,
                predicted: true,
                description: 'Predicted number of stories based on facade pictures'
              }
            }
            return { number_stories: null, predicted: false }
          },
          sortOrder: 'da',
          description: this.toggleableColumns[Column.NUMBER_STORIES].description
        },
        {
          field: 'avg_rent',
          name: Column.AVG_RENT,
          label: 'avg_rent',
          align: 'center',
          description:
            'Average rent for the unit type in the building. You can toggle between effective or asking rent, and select a date range.'
        },
        this.deleteEnabled
          ? {
              field: 'actions',
              name: Column.ACTIONS,
              label: '',
              align: 'center'
            }
          : null
      ]
        .filter((column) => {
          if (column) {
            return filterColumns(column.name, this.toggleableColumns, this.visibleColumns)
          }
          return false
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.name, columnB.name, this.columnOrder)
        }) as QTableColumn[]
    },
    columnOrder(): Column[] {
      return [Column.ADDRESS, ...this.visibleColumns, Column.AVG_RENT, Column.ACTIONS]
    },
    skeletonColumns() {
      return [
        {
          field: Column.SIMILARITY,
          intercomId: 'intercom-similarity',
          style: this.skeletonStyles.similarity
        },
        {
          field: Column.ESTIMATED_OCCUPANCY,
          intercomId: 'intercom-estimated_occupancy',
          style: this.skeletonStyles.estimated_occupancy
        },
        {
          field: Column.EXPOSURE_PERCENT,
          intercomId: 'intercom-exposure_percent',
          style: this.skeletonStyles.exposure_percent
        },
        {
          field: Column.YEAR_BUILT,
          intercomId: 'intercom-year_built',
          style: this.skeletonStyles.year_built
        },
        {
          field: Column.NUMBER_UNITS,
          intercomId: 'intercom-number_units',
          style: this.skeletonStyles.number_units
        },
        {
          field: Column.NUMBER_STORIES,
          intercomId: 'intercom-number_stories',
          style: this.skeletonStyles.stories
        }
      ]
        .filter((column) => {
          if (column) {
            return filterColumns(column.field, this.toggleableColumns, this.visibleColumns)
          }
          return false
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.field, columnB.field, this.columnOrder)
        })
    },
    taxAssessorOwnerByProperty() {
      return usePropertyReportStore().taxAssessorOwnerByProperty
    }
  },
  watch: {
    imageFile: {
      handler(val) {
        if (val) {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.tempImageFile = e.target?.result as string
          }
          reader.readAsDataURL(val)
        }
      },
      immediate: true
    }
  },
  methods: {
    isError,
    isLoaded,
    isLoading,
    haversineDist,
    parseISO,
    format,
    formatNumber,
    formatPercent,
    differenceInDays,
    humanizeQualityScore,
    humanizeReviewScore,
    humanizeString,
    hasConcessions,
    toTitleCase,
    formatOwnerName(owner: TaxAssessorOwnerV2) {
      // Use owner_name if available, otherwise construct from parts
      if (owner.owner_name) {
        return toTitleCase(owner.owner_name)
      }

      const parts = [
        owner.owner_first_name,
        owner.owner_middle_name,
        owner.owner_last_name,
        owner.owner_name_suffix
      ].filter((_) => _ !== undefined)

      const result = parts.length > 0 ? parts.join(' ') : 'Unknown Owner'
      return toTitleCase(result)
    },
    resetDragState() {
      this.currentDragIndex = -1
      this.draggedOverIndex = -1
    },
    sortingChanged(colName: string) {
      this.$tracker.changedSort('rent_comps', colName)
    },
    moveComp(fromIndex: number, toIndex: number) {
      if (this.subjectDetail) {
        fromIndex = fromIndex - 1
        toIndex = toIndex - 1
      }
      this.$emit('move-comp', fromIndex, toIndex)
    },
    async onUpdateModelValue(file: File | null) {
      if (!file) {
        return
      }
      this.compressingImage = true
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 768,
        useWebWorker: true,
        fileType: 'image/webp'
      }
      const compressedBlob = await imageCompression(file, options)
      // Convert to File with webp extension and type
      const webpFileName = file.name.replace(/\.[^/.]+$/, '.webp')
      this.imageFile = new File([compressedBlob], webpFileName, {
        type: 'image/webp',
        lastModified: file.lastModified
      })
      this.compressingImage = false
    },
    onRejected(rejectedItems: { failedPropValidation: string; file: { size: number } }[]) {
      if (rejectedItems.length > 0) {
        const reason = rejectedItems[0].failedPropValidation
        const uploadedSizeInBytes = rejectedItems[0].file.size
        const uploadedSizeInMB = uploadedSizeInBytes / 1024 / 1024
        if (reason === 'max-file-size') {
          this.$q.notify({
            message: `Please upload a smaller file (max 5MB, provided file is ${uploadedSizeInMB.toFixed(
              2
            )} MB)`,
            color: 'blue',
            icon: 'info'
          })
        } else {
          console.warn('Invalid file', rejectedItems)
          this.$q.notify({
            message: 'Invalid file',
            color: 'negative',
            icon: 'warning'
          })
        }
      }
    },
    showConcessionsPopup() {
      this.$tracker.openedPopup('specials')
    },
    addBuildingAmenity(amenity: BuildingAmenity) {
      this.editPropertyValue = {
        ...this.editPropertyValue,
        building_amenities: [...(this.editPropertyValue?.building_amenities || []), amenity]
      }
      this.buildingAmenitiesInput = null
    },
    addUnitAmenity(amenity: UnitAmenity) {
      this.editPropertyValue = {
        ...this.editPropertyValue,
        unit_amenities: [...(this.editPropertyValue?.unit_amenities || []), amenity]
      }
      this.unitAmenitiesInput = null
    },
    checkNumbers(lower: number, upper: number) {
      return (val: number | string) => {
        if (typeof val === 'string') {
          return 'Must be a number'
        }
        if (val < lower) {
          return `Must be at least ${lower}`
        }
        if (val > upper) {
          return `Must be at most ${upper}`
        }
        return true
      }
    },
    uploadFile() {
      if (this.imageFile) {
        this.editPropertyValue = {
          ...this.editPropertyValue,
          street_view_url: this.tempImageFile
        }
        this.tempImageFile = null
        this.uploadFileDialog = false
        this.imageFile = null
        if (this.editPropertyValue?.property_id) {
          delete this.streetviewErrors[this.editPropertyValue.property_id]
        }
      }
    },
    resetImage() {
      this.editPropertyValue = {
        ...this.editPropertyValue,
        street_view_url: null
      }
    },
    getStreetViewUrlStrict(prop: PropertyDetailsResponse) {
      return getStreetViewUrl(prop, import.meta.env.VITE_API_URL)
    },
    getStreetViewUrlUnion(prop: PropertyDetailsResponse | null) {
      if (prop === null) {
        return ''
      }
      if (prop.street_view_url) {
        return prop.street_view_url
      }

      return getStreetViewUrl(prop, import.meta.env.VITE_API_URL)
    },
    formatAmenity(amenity: BuildingAmenity | UnitAmenity) {
      return amenity
        .split('_')
        .map((_) => _.charAt(0).toUpperCase() + _.slice(1))
        .join(' ')
    },
    formatPredictedNumberUnits(val: number) {
      // write a bucket: 1, 2, 3-5, 6-10, 11-30, 31-100, 100-500, 500+
      if (val === 1) {
        return '1'
      }
      if (val === 2) {
        return '2'
      }
      if (val >= 3 && val <= 5) {
        return '3-5'
      }
      if (val >= 6 && val <= 10) {
        return '6-10'
      }
      if (val >= 11 && val <= 30) {
        return '11-30'
      }
      if (val >= 31 && val <= 100) {
        return '31-100'
      }
      if (val >= 101 && val <= 500) {
        return '101-500'
      }
      return '500+'
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      this.$emit('update-property-details', propertyId, key, value)
    },
    explanations(
      count: number,
      fromDate: Date | null,
      toDate: Date | null,
      prices: number[],
      sqfts: number[]
    ) {
      // eg: 14 units listed between [date] and [date], ranging in price from [$] to [$] and sqft from [sqft] to [sqft]
      let startDate = '6/17/2023'
      let endDate = 'yesterday'
      if (fromDate) {
        startDate = format(fromDate, 'M/d/yyyy')
      }
      if (toDate) {
        endDate = format(toDate, 'M/d/yyyy')
      }
      if (count === 0) {
        return `No units listed between ${startDate} and ${endDate}`
      }
      let priceExplanation = `Based on ${count} units listed between ${startDate} and ${endDate}`

      if (prices.length > 1) {
        priceExplanation += `, ranging in price from ${formatMoney(
          Math.min(...prices),
          0
        )} to ${formatMoney(Math.max(...prices), 0)}`
      }

      if (sqfts.length > 1) {
        priceExplanation += `, and sqft from ${formatNumber(Math.min(...sqfts))} to ${formatNumber(
          Math.max(...sqfts)
        )}`
      }

      return priceExplanation
    },
    computeAverages(row: PropertyDetailsResponse) {
      let options = [
        { label: 'Studio', beds: [0] },
        { label: '1 BR', beds: [1] },
        { label: '2 BR', beds: [2] },
        { label: '3 BR', beds: [3] }
      ]

      // Add additional options for BR counts if they are found in the subjectBeds
      // Will attempt to remove empty options like Studio or 1 BR options if not present
      for (const bed of [4, 5, 6]) {
        if (this.subjectBeds.has(bed)) {
          const firstEmptyOption = options.findIndex((option) => {
            const [optionBedCount] = option.beds
            return !this.subjectBeds.has(optionBedCount)
          })

          if (firstEmptyOption !== -1) {
            options.splice(firstEmptyOption, 1)
          }

          options = options.concat({ label: `${bed} BR`, beds: [bed] })
        }
      }

      const { units } = this.filteredProperties.find(({ prop }) => prop.id === row.id)!
      return options.map(({ label, beds }) => {
        const matchedUnits = units.filter(({ unit }) => {
          return unit.bed !== null && beds.includes(unit.bed)
        })

        const psfs: { price: number | null; sqft: number | null }[] = []
        const sqfts: number[] = []
        const prices: number[] = []

        for (const { unit, lastHistory } of matchedUnits) {
          const sqft = unit.sqft || unit.min_sqft
          const price =
            this.metricSelection.value === 'rent'
              ? (lastHistory.price ?? lastHistory.min_price)
              : (lastHistory.effective_price ?? lastHistory.min_effective_price)
          if (sqft) {
            sqfts.push(sqft)
          }
          if (price) {
            prices.push(price)
          }
          psfs.push({ price, sqft })
        }

        return {
          label,
          rent: prices.length ? formatMoney(avg(prices), 0) : null,
          sqft: sqfts.length ? formatNumber(avg(sqfts)) : null,
          psf: psfs.length ? formatMoney(avgPsf(psfs), 2) : null,
          explanation: this.explanations(
            matchedUnits.length,
            this.dateRange.from,
            this.dateRange.to,
            prices,
            sqfts
          )
        }
      })
    },
    cleanURL(url: string) {
      // if the url is not valid, return null. If missing http or https, add https
      if (!url) {
        return null
      }
      if (!url.match(/^(http|https):\/\//)) {
        return `https://${url}`
      }
      // check if the url is valid
      try {
        new URL(url)
        return url
      } catch {
        return null
      }
    },
    isValidURL(val: string): boolean {
      // check if it's a valid web address
      if (val && !val.startsWith('http')) {
        val = `https://${val}`
      }
      try {
        new URL(val)
        // make sure it's not a local address
        if (val.includes('localhost')) {
          return false
        }
        // make sure it has a domain
        if (val.split('.').length < 2) {
          return false
        }
        return true
      } catch {
        return false
      }
    },
    addNote(location: string, text: string) {
      this.$emit('add-note', {
        location,
        text
      })
    },
    deleteAddress(rowId: string) {
      if (this.subjectDetail && this.subjectDetail.id === rowId) {
        this.$q
          .dialog({
            title: 'Delete Address',
            message:
              'You are about to remove the subject property. Some features will no longer be available without a subject property.',
            cancel: true,
            persistent: true
          })
          .onOk(() => {
            this.$emit('delete-address', rowId)
          })
      } else {
        this.$emit('delete-address', rowId)
      }
    },
    computeStars(row: PropertyDetailsResponse): number | null {
      const quality = row.building_quality?.property_overall_quality
      if (quality != null) {
        return Math.round(quality / 0.2 / 0.5) * 0.5
      }
      return null
    },
    hasQualityScore(row: PropertyDetailsResponse): boolean {
      return row.building_quality ? row.building_quality.property_overall_quality !== null : false
    },
    qualityRows(row: PropertyDetailsResponse) {
      return Object.entries(row.building_quality ?? {})
        .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
        .map(([key, value]) => ({
          key,
          label: humanizeString(
            key.replace('avg_quality_score', '').replace('property_', '').replace('quality', '')
          ),
          score: Math.round(value * 10),
          isOverall: key === 'property_overall_quality'
        }))
    },
    openEditPropertyDialog(property: PropertyDetailsResponse) {
      this.hasScrolledUploadTerms = false
      this.editPropertyDialog = true
      this.editedProperty = property
      this.editPropertyValue = {
        property_id: property.id,
        building_name: property.building_name,
        year_built: property.year_built,
        number_units: property.number_units,
        number_stories: property.number_stories,
        cats_monthly_rent: property.cats_monthly_rent ?? 0,
        cats_one_time_fee: property.cats_one_time_fee ?? 0,
        cats_deposit: property.cats_deposit ?? 0,
        dogs_monthly_rent: property.dogs_monthly_rent ?? 0,
        dogs_one_time_fee: property.dogs_one_time_fee ?? 0,
        dogs_deposit: property.dogs_deposit ?? 0,
        admin_fee: property.admin_fee ?? 0,
        amenity_fee: property.amenity_fee ?? 0,
        application_fee: property.application_fee ?? 0,
        storage_fee: property.storage_fee ?? 0,
        parking_covered: property.parking_covered ?? 0,
        parking_garage: property.parking_garage ?? 0,
        parking_surface_lot: property.parking_surface_lot ?? 0,
        street_address: property.street_address,
        management_company: property.management_company,
        building_website: property.building_website,
        street_view_url: property.street_view_url,
        building_amenities: property.building_amenities ?? [],
        unit_amenities: property.unit_amenities ?? [],
        is_student: property.is_student ?? false,
        is_senior: property.is_senior ?? false,
        is_build_to_rent: property.is_build_to_rent ?? false,
        is_affordable: property.is_affordable ?? false,
        is_lease_up: property.is_lease_up ?? false,
        fees: property.fees ?? []
      }
    },
    confirmEditProperty() {
      if (this.editPropertyValue && this.editPropertyValue.property_id) {
        const propertyId = this.editPropertyValue.property_id

        // update the property website to include https:// if it doesn't already
        if (
          this.editPropertyValue.building_website &&
          !this.editPropertyValue.building_website.startsWith('http')
        ) {
          this.editPropertyValue.building_website = `https://${this.editPropertyValue.building_website}`
        }

        Object.entries(this.editPropertyValue).forEach(([key, value]) => {
          if (key !== 'property_id') {
            this.$emit('update-property-details', propertyId, key, value === '' ? null : value)
          }
        })
        this.editPropertyDialog = false
        this.editPropertyValue = null

        if (propertyId) {
          delete this.streetviewErrors[propertyId]
        }
      }
    },
    filterBuildingAmenities(val: string, updateFn: (fn: () => void) => void) {
      if (val === '') {
        updateFn(() => {
          this.buildingAmenitiesOptions = undefined
        })
        return
      }

      const needle = val.toLowerCase()

      updateFn(() => {
        if (this.buildingAmenitiesCache.length !== this.newBuildingAmenities.length) {
          this.buildingAmenitiesCache = this.newBuildingAmenities.map((amenity) => ({
            original: amenity,
            searchStr: amenity.toLowerCase().split('_').join(' ')
          }))
        }

        this.buildingAmenitiesOptions = this.buildingAmenitiesCache
          .filter(({ searchStr }) => searchStr.includes(needle))
          .map(({ original }) => original)
      })
    },

    filterUnitAmenities(val: string, updateFn: (fn: () => void) => void) {
      if (val === '') {
        updateFn(() => {
          this.unitAmenitiesOptions = undefined
        })
        return
      }

      const needle = val.toLowerCase()

      updateFn(() => {
        // Create cached lowercase versions
        if (this.unitAmenitiesCache.length !== this.newUnitAmenities.length) {
          this.unitAmenitiesCache = this.newUnitAmenities.map((amenity) => ({
            original: amenity,
            searchStr: amenity.toLowerCase().split('_').join(' ')
          }))
        }

        this.unitAmenitiesOptions = this.unitAmenitiesCache
          .filter(({ searchStr }) => searchStr.includes(needle))
          .map(({ original }) => original)
      })
    },
    createNewFee() {
      if (this.editPropertyValue?.fees) {
        this.$tracker.createNewFee()
        this.editPropertyValue.fees.push({
          label: `Fee #${this.editPropertyValue.fees.length + 1}`,
          amount: 0,
          frequency: 'month'
        })
      }
    },
    deleteFeeByIndex(index: number) {
      if (this.editPropertyValue?.fees) {
        this.editPropertyValue.fees.splice(index, 1)
      }
    },
    scrollDown() {
      const scrollArea = this.$refs.scrollAreaRef as QScrollArea
      scrollArea.setScrollPosition('vertical', 200, 300)
      this.hasScrolledUploadTerms = true
    },
    handleScroll(event: { verticalPosition: number }) {
      if (event.verticalPosition !== 0) {
        this.hasScrolledUploadTerms = true
      }
    },
    startDrag(index: number) {
      this.currentDragIndex = index
    },
    handleDragStart(event: DragEvent, index: number) {
      // Don't allow dragging if delete is not enabled
      if (this.currentDragIndex !== index) {
        event.preventDefault()
        return
      }

      if (!this.deleteEnabled) {
        event.preventDefault()
        return
      }

      // Don't allow dragging the subject property
      if (this.subjectDetail && this.rows[index].id === this.subjectDetail.id) {
        event.preventDefault()
        return
      }

      this.currentDragIndex = index
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
      }
    },
    canDropHere(dropIndex: number) {
      // Don't allow dropping on or before the subject property
      if (this.subjectDetail && dropIndex < 1) {
        return false
      }
      return true
    },
    handleDragOver(event: DragEvent, index: number) {
      if (index === this.draggedOverIndex) return
      if (!this.canDropHere(index)) {
        event.dataTransfer!.dropEffect = 'none'
        return
      }
      this.draggedOverIndex = index
      event.dataTransfer!.dropEffect = 'move'
    },
    handleDrop(dropIndex: number) {
      this.draggedOverIndex = -1

      // Don't allow dropping if delete is not enabled
      if (!this.deleteEnabled) {
        return
      }

      // Don't allow dropping on or before the subject property
      if (!this.canDropHere(dropIndex)) {
        return
      }

      if (this.currentDragIndex === -1) return

      this.$emit('move-comp', this.currentDragIndex, dropIndex)
      this.currentDragIndex = -1
    },
    fetchOwnerDataForProperty(prop: PropertyDetailsResponse) {
      this.$tracker.ownerDataBtnClicked(prop.id)
      usePropertyReportStore()
        .fetchTaxOwnerDataForProperty(prop)
        .catch((e) => {
          console.error(e)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.sticky-actions-table {
  thead tr:last-child th:last-child,
  td:last-child {
    position: sticky;
    -webkit-position: sticky;
    right: 0;
    z-index: 1;
  }
}

.predicted-number {
  text-decoration-line: underline;
  text-decoration-color: $accent;
  text-decoration-thickness: 1px;
}
.hd-icon {
  font-size: 1.1em;
}

.property-type-box {
  padding: 1px 12px 4px 12px;
  font-size: 11px;
}

.property-tags-container {
  position: relative;
  bottom: 30px;
  left: 5px;
  z-index: 1;
  width: 170px;
  .property-tags {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 2px;
    width: 100%;
  }
}

tr[draggable='true'] {
  &:hover {
    outline: 1px dashed rgba(100, 100, 255, 0.3);
  }
}

.dark tr[draggable='true'] {
  &:hover {
    outline: 1px dashed rgba(100, 100, 255, 0.5);
  }
}

.drop-target {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px dashed $primary;
    pointer-events: none;
    z-index: 2;
  }
}

.dark .drop-target {
  &::after {
    border-color: rgba(100, 100, 255, 0.7);
  }
}

.selectable-text {
  user-select: text;
  cursor: text;
}

.drag-handle {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 24px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  user-select: none;
  cursor: move;
}

.body--dark .drag-handle {
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}
</style>
